import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface DashboardProps {
  userProfile: any;
}

export default function Dashboard({ userProfile }: DashboardProps) {
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    pendingAssignments: 0,
    completedAssignments: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, [userProfile]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Lấy danh sách khóa học của giáo viên
      const coursesResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-courses?teacher_id=${encodeURIComponent(userProfile.id)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      let courses = [];
      let totalStudents = 0;

      if (coursesResponse.ok) {
        courses = await coursesResponse.json();
        // Tính tổng số học sinh từ tất cả các khóa học
        totalStudents = courses.reduce((sum: number, course: any) => sum + (course.student_count || 0), 0);
      }

      // Lấy danh sách bài tập của giáo viên
      const assignmentsResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-teacher-assignments?instructor_id=${encodeURIComponent(userProfile.id)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      let assignments = [];
      if (assignmentsResponse.ok) {
        assignments = await assignmentsResponse.json();
      }

      // Lấy danh sách bài nộp
      const submissionsResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-teacher-submissions?instructor_id=${encodeURIComponent(userProfile.id)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      let submissions = [];
      if (submissionsResponse.ok) {
        const submissionsData = await submissionsResponse.json();
        // Lấy data từ response object
        submissions = submissionsData.data || [];
      }

      // Tính số bài tập chưa chấm (pending) và đã chấm (completed)
      const pendingSubmissions = submissions.filter((s: any) => s.status === 'submitted' || s.status === 'pending').length;
      const gradedSubmissions = submissions.filter((s: any) => s.status === 'graded').length;

      // Tạo hoạt động gần đây từ submissions
      const activities = submissions
        .sort((a: any, b: any) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
        .slice(0, 5)
        .map((submission: any) => ({
          id: submission.id,
          type: submission.status === 'graded' ? 'graded' : 'submitted',
          student: submission.student_name || submission.student_email || 'Học sinh',
          assignment: submission.assignment_title || 'Bài tập',
          course: submission.course_title || 'Khóa học',
          time: submission.submitted_at,
          score: submission.score
        }));

      setStats({
        totalCourses: courses.length,
        totalStudents: totalStudents,
        pendingAssignments: pendingSubmissions,
        completedAssignments: gradedSubmissions
      });

      setRecentActivities(activities);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'submitted':
        return 'ri-file-upload-line text-blue-600';
      case 'graded':
        return 'ri-check-line text-green-600';
      default:
        return 'ri-information-line text-gray-600';
    }
  };

  const getActivityText = (activity: any) => {
    if (activity.type === 'submitted') {
      return `${activity.student} đã nộp bài "${activity.assignment}"`;
    } else if (activity.type === 'graded') {
      return `Đã chấm bài "${activity.assignment}" của ${activity.student} - Điểm: ${activity.score}`;
    }
    return '';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Vừa xong';
    if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;
    if (diffInHours < 24) return `${diffInHours} giờ trước`;
    if (diffInDays < 7) return `${diffInDays} ngày trước`;
    return date.toLocaleDateString('vi-VN');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Khóa học</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalCourses}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-book-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Học sinh</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Chờ chấm</p>
              <p className="text-3xl font-bold text-gray-900">{stats.pendingAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-yellow-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Đã chấm</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completedAssignments}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Hoạt động gần đây</h3>
        </div>
        <div className="p-6">
          {recentActivities.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-notification-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500">Chưa có hoạt động nào</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 pb-4 border-b last:border-b-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={getActivityIcon(activity.type)}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{getActivityText(activity)}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.course}</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatTimeAgo(activity.time)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
